import React, { useState, useContext } from "react"
import _ from "lodash"
import { useQuery } from "@apollo/client"

//gsql queries
import { LOAD_FORM } from "../../queries"

//Formik & Yup
import { Formik, Form } from "formik"
import * as Yup from "yup"

//Referal modules
import RefTextInput from "../../Input/RefTextInput"
import RefDropDown from "../../Input/RefDropDown"
import RefCheckBoxInput from "../../Input/RefCheckboxInput"
import RefMultiSelect from "../../Input/RefMultiSelect"
import RefTreeSelect from "../../Input/RefTreeSelect"
import {
  mapUserRoleToFriendlyName,
  getServiceCategoryNode,
} from "../../sharedFunctions"
import {
  referralsApiPractitionerRoleTypesGet,
  refApiServiceCategoriesGet,
  referralsApiReferralEntityGetByOrganization,
} from "../../../partnerAPI"

//PrimeReact
import { Button } from "primereact/button"

// Context
import { UserContext } from "../../../../../context/userContext"

// Styles
import refEntityStyles from "../../../../../styles/referrals/ReferralEntityForm.module.css"
import { TabPanel, TabView } from "primereact/tabview"
import { LogError } from "../../../../../helpers/logger"

export default function ReferralEntityUserForm({
  isInitialLoad,
  isLoading,
  partnerOrganizationId,
  userProfile,
  requireUserPassword,
  handleFormSubmit,
  handleCancel,
}) {
  const userCtx = useContext(UserContext)

  const usePartnerUserId =
    userCtx.session.user.staff_role === 9 ||
    userCtx.session.user.staff_role === 10
      ? userCtx.session.user.staff_id
      : "-7"
  const usePartnerOrganization = !!partnerOrganizationId
    ? partnerOrganizationId
    : userCtx.session.user.organization_id

  const [roleDefinition, setRoleDefinition] = useState(false)
  const [entityLocations, setEntityLocations] = useState([])
  const [practitionerRoleTypes, setPractitionerRoleTypes] = useState([])
  const [serviceCategories, setServiceCategories] = useState([])

  const [loadingForm, setLoadingForm] = useState(true)

  const validationSchema = Yup.object().shape({
    userProfile: Yup.object().shape({
      staff_email_address: Yup.string()
        .required("Please specify the e-mail address")
        .email("Please specify a valid e-mail address"),
      staff_organization_id: Yup.string().required(
        "Please specify an organization",
      ),
      staff_role: Yup.string().required("Please specify a user role"),
    }),
  })

  useQuery(LOAD_FORM, {
    variables: {
      uid: usePartnerUserId, //Use the staff ID if this is a partner user, else use the service user id of -7
      organization_id: usePartnerOrganization,
    },
    onCompleted: async (data) => {
      const roleOptions = _.map(data.roles, ({ rol_id, rol_name }) => {
        return {
          label: mapUserRoleToFriendlyName(rol_id, rol_name),
          value: rol_id,
        }
      })

      setRoleDefinition(roleOptions)

      let referralApiPromised = []

      referralApiPromised.push(
        referralsApiReferralEntityGetByOrganization(
          userCtx.tokens.getMainToken(),
          usePartnerOrganization,
        )
          .then((result) => {
            setEntityLocations(
              result.referralLocations.map((location) => {
                const locationProfile = location.location
                return {
                  referralLocationId: locationProfile.id,
                  name: locationProfile.name,
                  referralLocation: {
                    ...locationProfile,
                    referralEntity: null,
                  },
                }
              }),
            )
          })
          .catch((error) => {
            let message = "Unable to retrieve partner locations"

            if (!!error.isAxiosError) {
              message = `${message}: ${error.response.data}`
            }

            userCtx.setNotificationError(message)
            LogError("Unable to retrieve partner locations", error)
          }),
      )

      referralApiPromised.push(
        referralsApiPractitionerRoleTypesGet(userCtx.tokens.getMainToken())
          .then((result) => {
            setPractitionerRoleTypes(result)
          })
          .catch((error) => {
            LogError("Unable to load practitioner roles", error)
            userCtx.setNotificationError("Unable to load practitioner roles")
          }),
      )

      referralApiPromised.push(
        refApiServiceCategoriesGet(userCtx.tokens.getMainToken())
          .then((result) => {
            let nodes = result.map((cat) => getServiceCategoryNode(cat))
            setServiceCategories(nodes)
          })
          .catch((error) => {
            LogError("Unable to load service categories", error)
            userCtx.setNotificationError("Unable to load service categories")
          }),
      )

      await Promise.all(referralApiPromised).then(setLoadingForm(false))
    },
    onError: (error) => {
      LogError("Unable to load form data from server", error)
      userCtx.setNotificationError("Unable to load form data from server")
      setLoadingForm(false)
    },
  })

  async function handleFormikSubmit(values) {
    await handleFormSubmit({ variables: values.userProfile })
  }

  function LocationSelectionTemplate(option) {
    let label = option.name

    if (option.virtual) {
      label = `${label}: ${option.website}`
    } else if (!!option.noAddress) {
      label = `${label}: No address`
    } else {
      label = `${label}:  ${
        !!option.referralLocation.address
          ? option.referralLocation.address + ", "
          : ""
      } ${
        !!option.referralLocation.City
          ? option.referralLocation.City + ", "
          : ""
      } ${!!option.referralLocation.state ? option.referralLocation.state : ""}`
    }

    return <>{label}</>
  }

  function UserForm({ isSubmitting, values }) {
    return (
      <div className="p-fluid">
        <RefTextInput
          name="userProfile.staff_name_first"
          label="First name:"
          placeholderText="First name"
          required={true}
          isInitialLoad={isInitialLoad || loadingForm}
          disabled={isLoading}
        />
        <RefTextInput
          name="userProfile.staff_name_last"
          label="Last name"
          placeholderText="Last name"
          isInitialLoad={isInitialLoad || loadingForm}
          disabled={isLoading}
        />
        <RefTextInput
          name="userProfile.staff_email_address"
          label="Primary account email address:"
          placeholderText="E-mail"
          required={true}
          isInitialLoad={isInitialLoad || loadingForm}
          disabled={isLoading}
        />

        <RefDropDown
          name="userProfile.staff_role"
          label="Organization role"
          options={roleDefinition}
          disabled={isLoading}
          isInitialLoad={isInitialLoad || loadingForm}
        />

        <RefCheckBoxInput
          name="userProfile.isPractitioner"
          label="Is this a practitioner profile?"
          isInitialLoad={isInitialLoad || loadingForm}
          disabled={isLoading}
        />

        {values.userProfile?.isPractitioner ? (
          <>
            <RefTextInput
              name="userProfile.practitionerNpi"
              label="National provider identification:"
              placeholderText="National provider identification"
              required={values.userProfile?.isPractitioner}
              isInitialLoad={isInitialLoad || loadingForm}
              disabled={isLoading}
            />

            <RefMultiSelect
              name="userProfile.practitionerLocations"
              label="Practitioner locations:"
              placeHolderValue="Locations"
              required={values.userProfile?.isPractitioner}
              isInitialLoad={isInitialLoad || loadingForm}
              disabled={isLoading}
              options={entityLocations}
              optionLabel={"name"}
              itemTemplate={LocationSelectionTemplate}
            />

            <RefDropDown
              name="userProfile.practitionerRoleType"
              label="Practitioner roles:"
              placeHolderValue="Please select the practitioner role"
              required={values.userProfile?.isPractitioner}
              isInitialLoad={isInitialLoad || loadingForm}
              disabled={isLoading}
              options={practitionerRoleTypes}
              optionLabel={"displayName"}
              optionValue={"id"}
            />

            <RefTreeSelect
              name={`userProfile.practitionerReferralCategories`}
              label="Service categories"
              isInitialLoad={isInitialLoad || loadingForm}
              options={serviceCategories}
              disabled={isLoading}
            />
          </>
        ) : null}
      </div>
    )
  }

  return (
    <div style={{ display: "inline-block", width: "100%" }}>
      <Formik
        initialValues={{ userProfile: userProfile }}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleFormikSubmit}
      >
        {({ handleSubmit, isValid, isSubmitting, dirty, values, errors }) => (
          <Form onSubmit={handleSubmit} autoComplete="off">
            <div>
              {values.userProfile.uid === undefined ? (
                <UserForm isSubmitting={isSubmitting} values={values} />
              ) : (
                <TabView>
                  <TabPanel
                    header={
                      errors.referralEntity === undefined ? (
                        "Profile"
                      ) : (
                        <span>
                          Profile <span style={{ color: "red" }}> [!]</span>
                        </span>
                      )
                    }
                  >
                    <UserForm isSubmitting={isSubmitting} values={values} />
                  </TabPanel>
                </TabView>
              )}
            </div>

            <div className={refEntityStyles.footer}>
              <div
                className={
                  refEntityStyles.footer + " " + refEntityStyles.buttonContainer
                }
              >
                <Button
                  label="Save"
                  icon="pi pi-check"
                  type="submit"
                  disabled={!dirty || isLoading}
                  loading={isLoading}
                  style={{ marginRight: "0.5rem", width: "115px" }}
                  className={refEntityStyles.button}
                />
                <Button
                  label="Cancel"
                  icon="pi pi-times"
                  type="button"
                  className={refEntityStyles.button}
                  onClick={() => {
                    handleCancel()
                  }}
                  style={{ width: "115px" }}
                  disabled={isLoading}
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}
